import React from "react";
import { FiCheck, FiHome, FiInfo, FiMapPin, FiPackage } from "react-icons/fi";
import { FaFlask } from "react-icons/fa";
import { getCheckoutAoeQuestions } from "../labs/services/aoeNormalizer";
import {
  COLLECTION_METHOD_DESCRIPTIONS,
  COLLECTION_METHOD_BANNERS,
} from "../labs/services/labConstants";
import { aoeAnswersToDict } from "./programLabCheckout";

export const COLLECTION_METHOD_LABELS = {
  draw_site: "Visit a local draw site",
  walk_in_test: "Visit a local draw site",
  at_home_phlebotomy: "At-home phlebotomy visit",
  testkit: "At-home test kit",
  on_site_collection: "On-site collection",
};

export { COLLECTION_METHOD_BANNERS };

export function CollectionMethodIcon({ method, className = "" }) {
  const Icon = method === "testkit"
    ? FiPackage
    : method === "at_home_phlebotomy"
      ? FiHome
      : FiMapPin;
  return (
    <Icon
      aria-hidden="true"
      className={`lcw-collection-icon lcw-collection-icon--${method || "draw_site"} ${className}`.trim()}
      focusable="false"
    />
  );
}

export function PolicyModal({ policyDoc, onClose, onAgree }) {
  if (!policyDoc) return null;
  return (
    <div className="lcw-modal-backdrop" onClick={onClose}>
      <div className="lcw-modal" onClick={(e) => e.stopPropagation()}>
        <div className="lcw-modal-header">
          <div>
            <div className="lcw-modal-title">{policyDoc.title}</div>
            <span className="lcw-modal-key">
              {policyDoc.version ? `Version ${policyDoc.version}` : policyDoc.policy_type}
            </span>
          </div>
          <button type="button" className="lcw-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="lcw-modal-body">{policyDoc.content || ""}</div>
        <div className="lcw-modal-footer">
          <button type="button" className="lcw-modal-action lcw-modal-action--secondary" onClick={onClose}>Close</button>
          <button type="button" className="lcw-modal-action lcw-modal-action--primary" onClick={onAgree}>Agree &amp; check</button>
        </div>
      </div>
    </div>
  );
}

export function LabCollectionMethod({ options, selected, onSelect }) {
  return (
    <div className="program-lab-checkout__method">
      <span className="program-lab-checkout__method-label">Collection method</span>
      <div className="program-lab-checkout__method-options">
        {options.map((option) => {
          const opt = typeof option === "string" ? option : option.method;
          const isSelected = selected === opt || selected === option?.assignment_id;
          const label = option.label || COLLECTION_METHOD_LABELS[opt] || opt.replace(/_/g, " ");
          const desc = COLLECTION_METHOD_DESCRIPTIONS[opt] || "";
          return (
            <button
              type="button"
              key={opt}
              className={`lcw-method-card ${isSelected ? "selected" : ""}`}
              onClick={() => onSelect(option)}
            >
              <div className="lcw-method-radio">
                {isSelected && <div className="lcw-method-radio-dot" />}
              </div>
              <CollectionMethodIcon method={opt} />
              <div className="lcw-method-copy">
                <div className="lcw-method-title">{label}</div>
                {desc && <div className="lcw-method-desc">{desc}</div>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function LabAoeQuestions({ lab, selectedMethod, answers, onAnswer }) {
  const questions = getCheckoutAoeQuestions(lab, selectedMethod);
  const answerMap = aoeAnswersToDict(answers);
  if (!questions.length) return null;
  return (
    <div className="lcw-aoe-list">
      {questions.map((q) => {
        const key = `${q.markerId}:${q.questionId}`;
        const value = answerMap?.[key] || "";
        const selectedCodes = String(value || "")
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean);
        const usesChoiceChips = q.isChoice && q.options?.length <= 5;
        const usesMultiChoiceChips = q.isMultiChoice && q.options?.length <= 5;
        const labelTarget = usesChoiceChips || usesMultiChoiceChips ? undefined : `aoe-${key}`;

        return (
          <div key={key} className="lcw-aoe-card" data-aoe-key={key}>
            <div className="lcw-aoe-card-header">
              {q.biomarkerName && (
                <div className="lcw-aoe-biomarker-badge" title={q.biomarkerName}>
                  <FaFlask size={12} className="lcw-aoe-badge-icon" />
                  <span className="lcw-aoe-badge-text">{q.biomarkerName}</span>
                </div>
              )}
              {q.required ? (
                <div className="lcw-aoe-required">Required</div>
              ) : (
                <div className="lcw-aoe-optional">Optional</div>
              )}
            </div>

            <label
              id={`aoe-label-${key}`}
              className="lcw-aoe-question-label"
              htmlFor={labelTarget}
              title={q.label}
            >
              {q.label}
            </label>

            {q.helperText && (
              <div className="lcw-aoe-helper">
                <FiInfo size={14} />
                <span>{q.helperText}</span>
              </div>
            )}

            {usesChoiceChips ? (
              <div className="lcw-aoe-chip-group" role="radiogroup" aria-labelledby={`aoe-label-${key}`}>
                {q.options.map((opt) => {
                  const isSelected = value === opt.code;
                  return (
                    <button
                      key={opt.code}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      className={`lcw-aoe-chip ${isSelected ? "lcw-aoe-chip--selected" : ""}`}
                      onClick={() => onAnswer(key, opt.code)}
                    >
                      {isSelected && <FiCheck size={14} className="lcw-aoe-chip-icon" />}
                      <span>{opt.label || opt.code}</span>
                    </button>
                  );
                })}
              </div>
            ) : usesMultiChoiceChips ? (
              <div className="lcw-aoe-chip-group" role="group" aria-labelledby={`aoe-label-${key}`}>
                {q.options.map((opt) => {
                  const isSelected = selectedCodes.includes(opt.code);
                  return (
                    <button
                      key={opt.code}
                      type="button"
                      role="checkbox"
                      aria-checked={isSelected}
                      className={`lcw-aoe-chip ${isSelected ? "lcw-aoe-chip--selected" : ""}`}
                      onClick={() => {
                        const next = isSelected
                          ? selectedCodes.filter((c) => c !== opt.code)
                          : [...selectedCodes, opt.code];
                        onAnswer(key, next.join(","));
                      }}
                    >
                      {isSelected && <FiCheck size={14} className="lcw-aoe-chip-icon" />}
                      <span>{opt.label || opt.code}</span>
                    </button>
                  );
                })}
              </div>
            ) : q.isChoice ? (
              <select
                id={`aoe-${key}`}
                className="lcw-input"
                value={value}
                onChange={(e) => onAnswer(key, e.target.value)}
                required={q.required}
              >
                <option value="">Select…</option>
                {q.options.map((opt) => (
                  <option key={opt.code} value={opt.code}>
                    {opt.label || opt.code}
                  </option>
                ))}
              </select>
            ) : q.isMultiChoice ? (
              <div className="program-lab-checkout__aoe-options">
                {q.options.map((option) => {
                  const isChecked = selectedCodes.includes(option.code);
                  return (
                    <label key={option.code} className="lcw-consent-label">
                      <input
                        type="checkbox"
                        className="lcw-consent-check"
                        checked={isChecked}
                        onChange={() => {
                          const next = isChecked
                            ? selectedCodes.filter((c) => c !== option.code)
                            : [...selectedCodes, option.code];
                          onAnswer(key, next.join(","));
                        }}
                      />
                      <span>{option.label || option.code}</span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <input
                id={`aoe-${key}`}
                className="lcw-input"
                type={q.isNumeric ? "number" : "text"}
                value={value}
                onChange={(e) => onAnswer(key, e.target.value)}
                placeholder={q.placeholder || "Enter answer…"}
                required={q.required}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
export function ProgramLabSubsetActions({
  availability,
  onContinue,
  busy = false,
}) {
  const journey = availability?.journey;
  const programs = (availability?.programs || []).filter(
    (program) => journey?.ready_program_ids?.includes(String(program.program_id)),
  );
  if (
    !journey?.can_continue_with_subset
    || !programs.length
    || typeof onContinue !== "function"
  ) return null;
  const names = programs.map((program) => program.program_name).filter(Boolean);
  const label = names.length ? names.join(" and ") : "eligible Programs";
  return (
    <div className="lcw-subset-actions" role="group" aria-label="Eligible treatment Programs">
      <div className="lcw-subset-copy">
        <strong>{programs.length} selected {programs.length === 1 ? "Program" : "Programs"} can continue.</strong>
        <span>Unavailable Programs will be removed from this checkout.</span>
      </div>
      <button
        type="button"
        className="lcw-primary-btn"
        disabled={busy}
        onClick={() => onContinue()}
      >
        {busy ? "Updating…" : `Continue with ${label}`}
      </button>
    </div>
  );
}
